<script setup lang="ts">
/**
 * Step through periods: ‹ Heute › and the current period's name — a calendar
 * week, a month, an attendance list. Two ways to use it:
 *
 * - with `v-model` (a Date) and `unit`, it moves the date itself and writes
 *   the label („KW 39 · 21.–27. Sept. 2026“, „September 2026“, „Q3 2026“);
 *   „Heute“ is disabled while today is already in the shown period;
 * - without, it only emits `prev` / `next` / `today` and shows `label`.
 *
 * The events fire in both modes. The label is a polite live region, so the
 * new period is announced after a step. `hideLabel` hides it visually when the
 * page title already names the period — it stays the group's name and the live
 * region. `step` moves several units per click (a rolling week is `unit="day"`
 * with `step` 7); „Heute“ still compares the model's own `unit`.
 */
import ChevronLeft from 'lucide-vue-next/dist/esm/icons/chevron-left.js'; import ChevronRight from 'lucide-vue-next/dist/esm/icons/chevron-right.js';
import { computed, inject } from 'vue';
import { NxButton } from '../Button';
import { NxIconButton } from '../IconButton';
import { i18nInjectionKey } from '../internal';
import { periodLabel, samePeriod, shiftPeriod, type NxPeriodUnit } from './period';

const props = withDefaults(
  defineProps<{
    unit?: NxPeriodUnit;
    /** Units per click. Defaults to 1. */
    step?: number;
    /** Shown instead of the computed label (and when there is no `v-model`). */
    label?: string;
    /** Hides the label visually; it stays the group's name and the live region. */
    hideLabel?: boolean;
    prevLabel?: string;
    nextLabel?: string;
    todayLabel?: string;
    /** Calendar-week abbreviation in week labels. */
    weekPrefix?: string;
    /** Hides the „Heute“ button. */
    hideToday?: boolean;
    disabled?: boolean;
    /** Disables only the back step (no earlier period to show). */
    prevDisabled?: boolean;
    /** Disables only the forward step. */
    nextDisabled?: boolean;
    locale?: string;
  }>(),
  {
    unit: 'month',
    step: 1,
    label: undefined,
    hideLabel: false,
    prevLabel: 'Zurück',
    nextLabel: 'Weiter',
    todayLabel: 'Heute',
    weekPrefix: 'KW',
    hideToday: false,
    disabled: false,
    prevDisabled: false,
    nextDisabled: false,
    locale: undefined,
  }
);

const model = defineModel<Date | null>({ default: null });

const emit = defineEmits<{
  prev: [];
  next: [];
  today: [];
}>();

const i18n = inject(i18nInjectionKey, null);
const locale = computed(() => props.locale ?? i18n?.locale?.value);

const text = computed(() => {
  if (props.label) return props.label;
  return model.value ? periodLabel(model.value, props.unit, locale.value, props.weekPrefix) : '';
});
const showsToday = computed(() => !!model.value && samePeriod(model.value, new Date(), props.unit));
const stepSize = computed(() =>
  Number.isFinite(props.step) ? Math.max(1, Math.trunc(props.step)) : 1
);

function move(direction: -1 | 1) {
  if (model.value) model.value = shiftPeriod(model.value, props.unit, direction * stepSize.value);
  if (direction < 0) emit('prev');
  else emit('next');
}
function goToday() {
  if (model.value) model.value = new Date();
  emit('today');
}
</script>

<template>
  <div class="nx-period-navigator" role="group" :aria-label="text || undefined">
    <!-- No `title` on the arrows (28.09.2026, André, I-2175): a chevron needs no
         native browser tooltip, and a native one is not the kit's `NxTooltip`.
         The accessible name stays on `aria-label`. -->
    <div class="nx-period-navigator__steps">
      <NxIconButton
        height="s"
        :aria-label="props.prevLabel"
        :disabled="props.disabled || props.prevDisabled"
        data-testid="nx-period-prev"
        @click="move(-1)"
      >
        <ChevronLeft class="nxt-icon-md" aria-hidden="true" />
      </NxIconButton>
      <NxIconButton
        height="s"
        :aria-label="props.nextLabel"
        :disabled="props.disabled || props.nextDisabled"
        data-testid="nx-period-next"
        @click="move(1)"
      >
        <ChevronRight class="nxt-icon-md" aria-hidden="true" />
      </NxIconButton>
    </div>
    <NxButton
      v-if="!props.hideToday"
      height="s"
      variant="outline"
      :disabled="props.disabled || showsToday"
      data-testid="nx-period-today"
      @click="goToday"
    >
      {{ props.todayLabel }}
    </NxButton>
    <span
      class="nx-period-navigator__label"
      :class="{ 'is-hidden': props.hideLabel }"
      aria-live="polite"
      data-testid="nx-period-label"
    >
      {{ text }}
    </span>
  </div>
</template>

<style scoped>
.nx-period-navigator {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--nxt-space-xs) var(--nxt-space-sm);
  min-width: 0;
}
.nx-period-navigator__steps {
  display: inline-flex;
  align-items: center;
  overflow: hidden;
  border: var(--nxt-hairline) solid var(--nxt-border);
  border-radius: var(--nxt-radius-md);
  background: var(--nxt-surface-raised-bg);
}
.nx-period-navigator__steps > :first-child {
  border-inline-end: var(--nxt-hairline) solid var(--nxt-border);
}
.nx-period-navigator__label {
  min-width: 0;
  overflow: hidden;
  color: var(--nxt-fg);
  font-size: var(--nxt-font-size-md);
  font-weight: var(--nxt-font-weight-semibold);
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.01em;
  text-overflow: ellipsis;
  white-space: nowrap;
}
/* Visually hidden, still announced: the page title already names the period. */
.nx-period-navigator__label.is-hidden {
  position: absolute;
  width: var(--nxt-hairline);
  height: var(--nxt-hairline);
  margin: calc(var(--nxt-hairline) * -1);
  clip-path: inset(50%);
}
</style>
